import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { jwtDecode } from 'jwt-decode';

import { API_BASE_URL } from '../../config/apiBase';

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Token type interface
interface TokenPayload {
  exp: number;
  user_id: number;
  email: string;
  role: string;
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management functions
export const tokenManager = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  setTokens: (access: string, refresh: string): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  },

  clearTokens: (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // Also drop the cached `user` blob so AuthContext's
    // useState(getCurrentUser) initializer doesn't see a stale
    // identity on the next page load.
    localStorage.removeItem('user');
  },

  isTokenExpired: (token: string): boolean => {
    try {
      const decoded = jwtDecode<TokenPayload>(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch {
      return true;
    }
  },

  getUserFromToken: (): TokenPayload | null => {
    const token = tokenManager.getAccessToken();
    if (!token) return null;
    
    try {
      return jwtDecode<TokenPayload>(token);
    } catch {
      return null;
    }
  },
};

// Request interceptor
apiClient.interceptors.request.use(
  (config: AxiosRequestConfig): any => {
    const token = tokenManager.getAccessToken();

    if (token && !tokenManager.isTokenExpired(token)) {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Add language header
    const language = localStorage.getItem('language') || 'en';
    config.headers = config.headers || {};
    config.headers['Accept-Language'] = language;

    // If the request data is FormData, remove the Content-Type header
    // to let axios set it automatically with the correct boundary
    // Also store a reference to the FormData for potential retry
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      // Store FormData entries for retry (FormData is consumed after first use)
      (config as any)._formDataEntries = Array.from((config.data as FormData).entries());
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 errors (Unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = tokenManager.getRefreshToken();
      
      if (refreshToken && !tokenManager.isTokenExpired(refreshToken)) {
        try {
          const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem(ACCESS_TOKEN_KEY, access);

          // Retry original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${access}`;

          // If original request had FormData, recreate it from stored entries
          if (originalRequest._formDataEntries) {
            const newFormData = new FormData();
            for (const [key, value] of originalRequest._formDataEntries) {
              newFormData.append(key, value);
            }
            originalRequest.data = newFormData;
            // Remove Content-Type to let axios set boundary
            delete originalRequest.headers['Content-Type'];
          }

          return apiClient(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login — but only if we're
          // somewhere else. Re-asserting /login while already there
          // is what kicked off the flicker loop on 2026-06-22:
          // browser reloads the page, AuthContext sees stale
          // localStorage state, fires the same 401, repeat.
          tokenManager.clearTokens();
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token or expired, redirect to login
        tokenManager.clearTokens();
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }

    // HTTP 409 with tenant_tap_not_configured — the tenant's TAP credentials
    // are missing. Surface a custom event so any payment-initiating page
    // can route the user to /settings/payments/tap with a friendly prompt.
    if (
      error.response?.status === 409
      && error.response?.data?.errors?.code === 'tenant_tap_not_configured'
    ) {
      window.dispatchEvent(new CustomEvent('nuagent:tap_not_configured', {
        detail: error.response.data.errors,
      }));
    }

    return Promise.reject(error);
  }
);

// API client export
export default apiClient;

// Common API methods
export const api = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.get(url, config),
    
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.post(url, data, config),
    
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.put(url, data, config),
    
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.patch(url, data, config),
    
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    apiClient.delete(url, config),
};